import { InputGroup } from "@components/_Revamp/InputGroup";
import { Title } from "@components/_Revamp/Typography/Title.stories";
import { styled } from "@linaria/react";
import MainContainer from "@pages/EverGreen/Components/Container";
import FlatButton from "@pages/EverGreen/Components/FlatButton";
import InnerContainer from "@pages/EverGreen/Components/InnerContainer";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";
import {
  updateDeclarationResponse,
  updatePEPstatus,
} from "@redux/slices/EVER_GREEN/step3Slice";
import { ROUTE_PATH } from "@static/route-paths";
import { SCREEN_SIZE } from "@static/screen-size";
import { EverGreenStyle } from "@utils/Style/everGreen";
import React, {
  ButtonHTMLAttributes,
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from "react";
import PEPForm from "./PEPForm";
import Button from "@pages/EverGreen/Components/Button";
import { Tooltip } from "@pages/EverGreen/Components/Tooltip";
import { css, cx } from "@linaria/core";
import { useHistory } from "react-router-dom";
import { postUserInfoSave } from "@api/index";
import {
  IEverGreenMailingAddress,
  IMailingAddress,
  IOtherCountryTax,
  ISaveInfoPayload,
} from "@utils/interfaces";
import { LoadingScreen } from "@utils/helpers";
import { trackOnFormStartOrSuccess, trackOnLoad } from "@pages/EverGreen/Helpers/analyticsHelper";

function PEP() {
  const isPEP = useAvivaSelector((state) => state.everGreenStep3.PEP.isPEP);
  const formData = useAvivaSelector(
    (state) => state.everGreenStep3.PEP.PEPData
  );
  const { everGreenStep2, everGreenStep3 } = useAvivaSelector((state) => state);
  const transactionId = useAvivaSelector(
    (state) => state.everGreenStep1.transactionId || ""
  );
  const dispatch = useAvivaDispatch();
  const [isDisabled, setDisable] = useState(true);
  const history = useHistory();
  const handleIsPep = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    trackOnFormStartOrSuccess("DS_pep_form")
    const target = e.target as HTMLButtonElement;
    dispatch(updatePEPstatus(target.value as "YES" | "NO"));
  }, []);
  const onClickNext = async () => {
    try {
      LoadingScreen();
      const { postal, ...address } = everGreenStep2.myInfo.address;
      const mailingAddress = everGreenStep2.myInfo.mailingAddress;
      let updatedMailingAddress: IEverGreenMailingAddress | null = null;
      let otherTaxs: IOtherCountryTax[] = everGreenStep3.tax.otherTax?.map(
        (tax) => ({
          ...tax,
          reasonForNoTaxIdentificationNumber:
            tax.reasonForNoTaxIdentificationNumber?.split("|")?.[0],
        })
      ) as unknown as IOtherCountryTax[];
      const annualIncome = everGreenStep3.occupation.annualIncome.id || "";
      const sourceOfWealth = `EMP_INC,${
        everGreenStep3.occupation.sourceOfWealth.ET ? "Y" : "N"
      }|REN_INC,${
        everGreenStep3.occupation.sourceOfWealth.Rental ? "Y" : "N"
      }|INV_INC,${
        everGreenStep3.occupation.sourceOfWealth.Invest ? "Y" : "N"
      }|OTH,${(everGreenStep3.occupation.sourceOfWealth.Others && everGreenStep3.occupation.sourceOfWealthOthersContent) || "-"}`;
      const sourceOfFund = `EMP_INC,${
        everGreenStep3.occupation.sourceOfFunds.ET2 ? "Y" : "N"
      }|SAL_PRO,${
        everGreenStep3.occupation.sourceOfFunds.SaleOfProperty ? "Y" : "N"
      }|SAV,${
        everGreenStep3.occupation.sourceOfFunds.Savings ? "Y" : "N"
      }|MAT_POL,${
        everGreenStep3.occupation.sourceOfFunds.MaturityOfPolicy ? "Y" : "N"
      }|OTH,${(everGreenStep3.occupation.sourceOfFunds.Others2 && everGreenStep3.occupation.sourceOfFundsOthersContent) || "-"}`;
      if (mailingAddress?.postal) {
        updatedMailingAddress = {
          block: mailingAddress?.block || "",
          building: mailingAddress?.building || "",
          country: mailingAddress?.country || "",
          postalCode: mailingAddress?.postal || "",
          street: mailingAddress?.street || "",
          unitNo: mailingAddress?.unitNo || "",
          useMailAddressForAll: mailingAddress?.useMailAddressForAll
            ? "Y"
            : "N",
        };
      }
      if (annualIncome === "" || !transactionId) {
        trackOnLoad({errorPage:{errorCode:"", errorMsg:"Failed due to invalid transaction id or annual income"}})
        // TODO route to error page
        history.push(ROUTE_PATH.EVER_GREEN_CONTACTUS);
        return;
      }
      //Annual income
      const payload: ISaveInfoPayload = {
        customerId: everGreenStep2.myInfo.customerId,
        transactionId,
        occupation: {
          code: everGreenStep3.occupation.selectedOccupation.id,
          desc: everGreenStep3.occupation.selectedOccupation.label,
        },
        addressData: {
          residentialAddress: { postalCode: postal, ...address },
          mailingAddress: updatedMailingAddress,
          isSameAsResidentialAddress: mailingAddress ? "N" : "Y",
        },
        annualIncome,
        declarationData: {
          isPep: everGreenStep3.PEP.isPEP === "YES" ? "Y" : "N",
          namePep: everGreenStep3.PEP.PEPData?.name || "",
          pepRelation: everGreenStep3.PEP.PEPData?.relationship || "",
          otherRelation: everGreenStep3.PEP.PEPData?.othersContent || "",
          fccOption: everGreenStep3.occupation.fcc === "YES" ? "Y" : "N",
          hasSingaporeTaxNumber: everGreenStep3.tax.hasSingaporeTaxNumber,
          singaporeTaxNumber: everGreenStep3.tax.singaporeTaxNumber || "",
          otherCountryTax: otherTaxs,
          reasonForNoTaxIdentificationNumber:
            everGreenStep3.tax.reasonForNoTaxIdentificationNumber?.split(
              "|"
            )?.[0],
          reasonExplanation: everGreenStep3.tax.reasonExplanation,
          crsOtherReason: everGreenStep3.tax.crsOtherReason,
          crsReason: everGreenStep3.tax.crsReason,
        },
        nameOfEmployer: everGreenStep3.occupation.employerName || "",
        natureOfBusiness: parseInt(
          everGreenStep3.occupation.natureOfBusiness.id
        ),
        sourceOfFund,
        sourceOfWealth,
      };
      const result = await postUserInfoSave(payload);
      dispatch(updateDeclarationResponse(result.data));
      trackOnFormStartOrSuccess("DS_pep_form",true)

      history.push(ROUTE_PATH.EVER_GREEN_DOCUMENT_REVIEW);
    } catch (e) {
      //TODO route to declaration page
      trackOnLoad({errorPage:{errorCode:"500", errorMsg:"Failed during to pep confirmation api call"}})

      history.push(ROUTE_PATH.EVER_GREEN_CONTACTUS);
    } finally {
      LoadingScreen(false);
    }

    //TODO route to declaration page

    // history.push(ROUTE_PATH.)
  };

  useEffect(() => {
    if (isPEP === null) {
      setDisable(true);
    } else if (
      isPEP === "YES" &&
      (!formData?.name ||
        !formData.relationship ||
        ((formData.relationship?.toLowerCase() === "others" ||
          formData.relationship?.toLowerCase() === "10") &&
          !formData.othersContent?.trim()?.length))
    ) {
      setDisable(true);
    } else {
      setDisable(false);
    }
  }, [formData, isPEP]);

    //AA on load
    useEffect(()=>{
      if (everGreenStep3.tax.hasSingaporeTaxNumber === null)
        return;

      trackOnLoad()
    },[])

  if (everGreenStep3.tax.hasSingaporeTaxNumber === null) {
    history.push(ROUTE_PATH.EVER_GREEN_TAX_DECLARATION);
    return null;
  }

  return (
    <MainContainer
      backButtonPath={ROUTE_PATH.EVER_GREEN_TAX_DECLARATION}
      step={3}
    >
      <TitleStyle>
        <span>Declare your </span>
        <span>
          <Tooltip
            title="Politically Exposed Person (PEP)"
            isIcon={false}
            inlineComponent={
              <span id="PEPText">Politically Exposed Person</span>
            }
          >
            <ul
              style={{
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "22.4px",
              }}
            >
              <li>
                A <b>'PEP'</b> is a person who is or has been entrusted with prominent
                public functions.
              </li>
              <li>
                A <b>'prominent public function'</b> includes the roles held by a head
                of state, a head of government, government ministers, senior
                civil or public servants, senior judicial or military officials,
                senior executives of state-owned corporations, senior political
                party officials, members of the legislature and senior
                management of international organisations.
              </li>
              <li>
                A <b>'close associate'</b> is a person who is closely connected to a
                politically exposed person, either socially or professionally.
              </li>
            </ul>
          </Tooltip>
        </span>
        <span>(PEP) status</span>
      </TitleStyle>
      <Description14>
      Ensure that the information you provide us is accurate and up to date.
      </Description14>
      <InnerContainer style={{ marginTop: "36px" }}>
        <Text>
        Are you or any immediate family member a PEP; or are you a close associate of a PEP?
        </Text>
        <ButtonContainer onClick={handleIsPep}>
          <FlatButton value={"NO"} maxWidth="352" isActive={isPEP === "NO"}>
            No
          </FlatButton>
          <FlatButton value={"YES"} maxWidth="352" isActive={isPEP === "YES"}>
            Yes
          </FlatButton>
        </ButtonContainer>
        <FormContainer
          show={isPEP === "YES"}
          full={
            formData?.relationship?.toLowerCase() === "others" ||
            formData?.relationship?.toLowerCase() === "10"
          }
          className={cx(
            "",
            (formData?.relationship?.toLowerCase() === "others" ||
              formData?.relationship?.toLowerCase() === "10") &&
              FormContainerAnimation
          )}
        >
          <PEPForm show={isPEP === "YES"}/>
        </FormContainer>
      </InnerContainer>
      <Button
        disabled={isDisabled}
        onClick={onClickNext}
        margin={"64px auto"}
        label="Next"
        width="180px"
      />
    </MainContainer>
  );
}

export default PEP;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    flex-direction: row;
  }
`;
const FormContainer = styled.div<{ show: boolean; full: boolean }>`
  margin-top: 10px;
  transition: height 0.3s linear 0.1s;
  height: ${(props) => (props.show ? (props.full ? "450px" : "250px") : "0px")};
  overflow: ${(props) => (props.show ? "unset" : "hidden")};
 
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    height: ${(props) =>
      props.show ? (props.full ? "350px" : "135px") : "0px"};
  }
`;

const FormContainerAnimation = css`
  animation: formanimation forwards 0.3s;
  @keyframes formanimation {
    0% {
      overflow: hidden;
    }
    99% {
      overflow: hidden;
    }
    100% {
      overflow: unset;
    }
  }
`;

const Text = styled.p`
  color: ${EverGreenStyle.colors.grey[500]};
  font-weight: 500;
  letter-spacing: 0.21px;
  font-size: ${EverGreenStyle.fontSize.description}px;
  line-height: 140%;
  margin-bottom: 16px;
`;

const TitleStyle = styled.p`
  width: 100%;
  gap: 5px;
  margin-bottom: 16px;
  font-size: 20px;
  font-weight: 600;
  color: ${EverGreenStyle.colors.grey[500]};
  text-align: center;
  line-height: 140%;
  b {
    font-weight: 600;
  }
  #PEPText {
    font-size: 20px;
    font-weight: 600;
    color: ${EverGreenStyle.colors.primary};
    text-align: center;
    line-height: 140%;
    text-underline-offset: 5px;
    text-decoration-style: dotted;
    text-decoration-line: underline;
    font-weight: 600;
  }
  p {
    font-size: ${EverGreenStyle.fontSize.xs}px;
  }
  @media screen and (min-width: 768px) {
    font-size: 28px;

    #PEPText {
      font-size: 28px;
      text-underline-offset: 5px;
      text-decoration-style: dotted;
      text-decoration-line: underline;
      font-weight: 600;
    }
    p {
      font-size: ${EverGreenStyle.fontSize.xs}px;
    }
  }
`;

const Description14 = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${EverGreenStyle.colors.grey[1200]};
  text-align: center;
`